import React, { useState } from 'react'
import { useAuthContext } from '../contexts/AuthContext'
import * as geminiService from '../services/geminiService'

import { FiCheck, FiX, FiLoader, FiAlertCircle } from 'react-icons/fi'

const ImageGenTestPage = () => {
  const { token } = useAuthContext()
  const [tests, setTests] = useState([
    {
      id: 'models',
      name: 'Get Available Models',
      status: 'pending',
      result: null,
    },
    {
      id: 'textToImage',
      name: 'Text to Image Generation',
      status: 'pending',
      result: null,
    },
    { id: 'imageEdit', name: 'Image Editing', status: 'pending', result: null },
    {
      id: 'multiImage',
      name: 'Multi-Image Generation',
      status: 'pending',
      result: null,
    },
  ])
  const [isRunning, setIsRunning] = useState(false)

  const updateTestStatus = (testId, status, result = null) => {
    setTests(prev =>
      prev.map(test => (test.id === testId ? { ...test, status, result } : test))
    )
  }

  const createTestImage = () => {
    const canvas = document.createElement('canvas')
    canvas.width = 100
    canvas.height = 100
    const ctx = canvas.getContext('2d')

    // Draw a simple test pattern
    ctx.fillStyle = '#FF0000'
    ctx.fillRect(0, 0, 50, 50)
    ctx.fillStyle = '#00FF00'
    ctx.fillRect(50, 0, 50, 50)
    ctx.fillStyle = '#0000FF'
    ctx.fillRect(0, 50, 50, 50)
    ctx.fillStyle = '#FFFF00'
    ctx.fillRect(50, 50, 50, 50)

    return new Promise(resolve => {
      canvas.toBlob(blob => {
        const file = new File([blob], 'test-image.png', { type: 'image/png' })
        resolve(file)
      })
    })
  }

  const runTests = async () => {
    setIsRunning(true)

    // Test 1: Get Available Models
    try {
      updateTestStatus('models', 'running')
      const models = await geminiService.getGeminiModels(token)
      updateTestStatus(
        'models',
        'success',
        `Found ${models.models?.length || 0} models`
      )
    } catch (error) {
      updateTestStatus('models', 'error', error.message)
    }

    // Test 2: Text to Image Generation
    try {
      updateTestStatus('textToImage', 'running')
      const response = await geminiService.generateImageFromText(
        token,
        'A simple red circle on white background'
      )
      updateTestStatus(
        'textToImage',
        'success',
        `Generated ${response.images?.length || 0} images`
      )
    } catch (error) {
      updateTestStatus('textToImage', 'error', error.message)
    }

    // Test 3: Image Editing
    try {
      updateTestStatus('imageEdit', 'running')
      const testImage = await createTestImage()
      const response = await geminiService.editImageWithText(
        token,
        'Make the background blue',
        testImage
      )
      updateTestStatus(
        'imageEdit',
        'success',
        `Edited image, got ${response.images?.length || 0} results`
      )
    } catch (error) {
      updateTestStatus('imageEdit', 'error', error.message)
    }

    // Test 4: Multi-Image Generation
    try {
      updateTestStatus('multiImage', 'running')
      const testImage1 = await createTestImage()
      const testImage2 = await createTestImage()
      const response = await geminiService.generateFromMultipleImages(
        token,
        'Combine these images into a collage',
        [testImage1, testImage2]
      )
      updateTestStatus(
        'multiImage',
        'success',
        `Generated ${response.images?.length || 0} images from multiple inputs`
      )
    } catch (error) {
      updateTestStatus('multiImage', 'error', error.message)
    }

    setIsRunning(false)
  }

  const getStatusIcon = status => {
    switch (status) {
      case 'success':
        return <FiCheck className="status-icon success" />
      case 'error':
        return <FiX className="status-icon error" />
      case 'running':
        return <FiLoader className="status-icon running" />
      default:
        return <FiAlertCircle className="status-icon pending" />
    }
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Image Generation Test Suite</h1>
      <p>Test all image generation endpoints to verify integration.</p>

      <button
        onClick={runTests}
        disabled={isRunning}
        style={{
          padding: '0.75rem 1.5rem',
          marginBottom: '2rem',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: isRunning ? 'not-allowed' : 'pointer',
        }}
      >
        {isRunning ? 'Running Tests...' : 'Run All Tests'}
      </button>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {tests.map(test => (
          <div
            key={test.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              backgroundColor:
                test.status === 'success'
                  ? '#f8f9fa'
                  : test.status === 'error'
                  ? '#fff5f5'
                  : 'white',
            }}
          >
            {getStatusIcon(test.status)}
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{test.name}</h3>
              {test.result && (
                <p
                  style={{
                    margin: '0.5rem 0 0 0',
                    fontSize: '0.9rem',
                    color: '#666',
                  }}
                >
                  {test.result}
                </p>
              )}
            </div>
            <span
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                backgroundColor:
                  test.status === 'success'
                    ? '#d4edda'
                    : test.status === 'error'
                    ? '#f8d7da'
                    : test.status === 'running'
                    ? '#d1ecf1'
                    : '#e2e3e5',
                color:
                  test.status === 'success'
                    ? '#155724'
                    : test.status === 'error'
                    ? '#721c24'
                    : test.status === 'running'
                    ? '#0c5460'
                    : '#495057',
              }}
            >
              {test.status.toUpperCase()}
            </span>
          </div>
        ))}
      </div>

      <style jsx>{`
        .status-icon {
          font-size: 1.5rem;
        }
        .status-icon.success {
          color: #28a745;
        }
        .status-icon.error {
          color: #dc3545;
        }
        .status-icon.running {
          color: #17a2b8;
          animation: spin 1s linear infinite;
        }
        .status-icon.pending {
          color: #6c757d;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  )
}

export default ImageGenTestPage
